import { useTranslation } from 'react-i18next';

import type { TooltipSize } from '~/components/Tooltip';
import { Tooltip } from '~/components/Tooltip';
import { cn } from '~/lib/cn';

export interface ItemPlusMoreProps<ItemNameKey extends string> {
  items: { [K in ItemNameKey]: string }[];
  /**
   * The property key in the items that contains the name to display.
   * If not provided, defaults to 'name'.
   */
  itemNameKey?: ItemNameKey;
  className?: string;
  itemClassName?: string;
  moreClassName?: string;
  tooltipSize?: TooltipSize;
}

export function ItemPlusMore<ItemNameKey extends string>({
  items,
  itemNameKey,
  className,
  itemClassName,
  moreClassName,
  tooltipSize = 'sm',
}: ItemPlusMoreProps<ItemNameKey>) {
  const { t: tGrammar } = useTranslation('grammar');
  const { t: tCommon } = useTranslation('common');

  if (!items.length) {
    return null;
  }

  const nameKey = itemNameKey ?? ('name' as ItemNameKey);

  return (
    <div className={cn('flex gap-sm', className)}>
      <span className={cn('text-body-md font-normal', itemClassName)}>
        {items[0][nameKey]}
      </span>
      {items.length > 1 ? (
        <Tooltip
          content={items
            .slice(1)
            .map((item) => item[nameKey])
            .join(tGrammar('commaSpace'))}
          delayDuration={0}
          side="top"
          triggerAsChild
          size={tooltipSize}
        >
          <span
            className={cn(
              'cursor-pointer text-body-sm font-semibold underline decoration-1 underline-offset-4 decoration-subtle',
              moreClassName,
            )}
          >
            {tCommon('plusCountMore', {
              count: items.length - 1,
            })}
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

ItemPlusMore.displayName = 'ItemPlusMore';
